import {
  Avatar,
  Button,
  ColumnGroup,
  CustomColumnDef,
  DataTable,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  LoadingIndicator,
} from '@commenda/kakashi';
import { IServiceRequest } from '@commenda/types/src/service-request/types';
import { ServiceRequestModel } from '@commenda/utils';
import { useMemo } from 'react';

interface ReviewStepProps {
  selectedServiceRequests: IServiceRequest[];
  isSentAsRequestingUser?: boolean;
  confirmText: string;
  setConfirmText: (text: string) => void;
  isSending: boolean;
  onBack: () => void;
  onSend: () => void;
}

export const ReviewStep = ({
  selectedServiceRequests,
  isSentAsRequestingUser = false,
  confirmText,
  setConfirmText,
  isSending,
  onBack,
  onSend,
}: ReviewStepProps) => {
  const reviewColumns: CustomColumnDef<IServiceRequest, unknown>[] = useMemo(() => [
    {
      id: 'serviceRequest',
      accessorKey: 'id',
      header: 'Service Request',
      cell: ({ row }) => (
        <div className="max-w-[220px] truncate">
          {ServiceRequestModel.getDisplayName(row.original)}
        </div>
      ),
    },
    {
      id: 'corporation',
      accessorKey: 'corporationName',
      header: 'Corporation',
      cell: ({ row }) => <span>{row.original.corporationName || 'N/A'}</span>,
    },
    {
      id: 'company',
      accessorKey: 'companyName',
      header: 'Company',
    },
    {
      id: 'sender',
      accessorFn: (row) => row.assignedAgent?.user?.name ?? 'N/A',
      header: 'Sender',
      cell: ({ row }) => (
        <div className="flex items-center gap-sm">
          {row.original.assignedAgent?.user?.image ? (
            <Avatar
              size="xs"
              image={row.original.assignedAgent.user.image}
              altText={row.original.assignedAgent.user.name ?? 'Agent'}
            />
          ) : (
            <Avatar
              size="xs"
              name={row.original.assignedAgent?.user?.name ?? '?'}
            />
          )}
          <span>{row.original.assignedAgent?.user?.name ?? 'N/A'}</span>
        </div>
      ),
    },
  ].filter((column) => !isSentAsRequestingUser || column.id !== 'sender'), [isSentAsRequestingUser]);

  const reviewColumnGroups: ColumnGroup<IServiceRequest, unknown>[] = useMemo(() => [
    { id: 'main', columns: reviewColumns },
  ], [reviewColumns]);

  return (
    <>
      <DialogHeader className="flex flex-col gap-xs items-start">
        <DialogTitle>Review & Confirm</DialogTitle>
        <DialogDescription>
          {isSentAsRequestingUser
            ? 'Review the recipients below. Each message will be sent from you.'
            : 'Review the recipients below. Each message will be sent from the assigned agent firm user.'}
        </DialogDescription>
      </DialogHeader>

      <div className="px-2xl py-xl flex flex-col gap-2xl min-w-0 overflow-y-auto flex-1">
        <div className="overflow-x-auto rounded-md border border-border-primary">
          <DataTable
            columnGroups={reviewColumnGroups}
            data={selectedServiceRequests}
          />
        </div>

        <div className="shrink-0">
          <label className="txt-small-medium text-text-secondary block mb-sm">
            Type &quot;I confirm&quot; to enable sending
          </label>
          <Input
            placeholder='I confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="secondary"
          onClick={onBack}
          disabled={isSending}
        >
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onSend}
          disabled={confirmText !== 'I confirm' || isSending}
        >
          {isSending ? (
            <div className="flex items-center gap-sm">
              <LoadingIndicator size={16} />
              Starting...
            </div>
          ) : (
            `Send ${selectedServiceRequests.length} Message(s)`
          )}
        </Button>
      </DialogFooter>
    </>
  );
};
